import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { Box, Button, Card, Grid, LinearProgress, Typography } from '@mui/material';
import {
  CompleteOrganizationOwnerInvitation,
  GetOrganizationOwnerInvitationStatus
} from 'Apollo';
import { Imgs } from 'Assets';
import useNotification from 'Hooks/useNotification';
import useTranslate from 'Hooks/useTranslate';
import { PageError } from 'Page/PageError';
import { useCallback, useState } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import { PrivateRoutes } from 'Router/Routes';

type Props = {
  token: string | null;
};

type StatusData = {
  organizationOwnerInvitationStatus: {
    isValid: boolean;
    isExpired: boolean;
    accountLabel: string | null;
  };
};

const OrganizationOwnerInvitation = ({ token }: Props) => {
  const ts = useTranslate();
  const history = useHistory();
  const { onError, onSuccess } = useNotification();
  const [completing, setCompleting] = useState(false);

  const { data, loading, error } = useQuery<StatusData>(
    GetOrganizationOwnerInvitationStatus,
    {
      variables: { token: token ?? '' },
      skip: !token,
      fetchPolicy: 'network-only'
    }
  );

  const [completeMutation] = useMutation(CompleteOrganizationOwnerInvitation);

  const onConfirm = useCallback(async () => {
    if (!token) {
      return;
    }
    setCompleting(true);
    try {
      await completeMutation({ variables: { token } });
      onSuccess('page.organizationOwnerInvitation.success');
      history.push(
        `${generatePath(PrivateRoutes.organizations.path, { accountCode: 'all' })}?token=${encodeURIComponent(token)}`
      );
    } catch (e) {
      onError(e as ApolloError);
    } finally {
      setCompleting(false);
    }
  }, [completeMutation, history, onError, onSuccess, token]);

  if (!token) {
    return (
      <PageError
        title={ts('page.organizationOwnerInvitation.missingTokenTitle')}
        textPrimary={ts('page.organizationOwnerInvitation.missingToken')}
      />
    );
  }

  if (loading) {
    return <LinearProgress color="secondary" />;
  }

  if (error || !data) {
    return (
      <PageError
        title={ts('error.somethingHappened')}
        textPrimary={ts('page.organizationOwnerInvitation.loadError')}
      />
    );
  }

  const status = data.organizationOwnerInvitationStatus;

  if (status.isExpired) {
    return (
      <PageError
        title={ts('page.organizationOwnerInvitation.expiredTitle')}
        textPrimary={ts('page.organizationOwnerInvitation.expired')}
      />
    );
  }

  if (!status.isValid) {
    return (
      <PageError
        title={ts('page.organizationOwnerInvitation.invalidTitle')}
        textPrimary={ts('page.organizationOwnerInvitation.invalidToken')}
      />
    );
  }

  const accountLabel = status.accountLabel ?? '';

  return (
    <Grid
      container
      alignItems="center"
      justifyContent="center"
      style={{ minHeight: '100vh' }}
    >
      <Grid item>
        <Card>
          <Box p={4} maxWidth={480}>
            <Box pb={3}>
              <img
                src={Imgs.cegid.src}
                alt={Imgs.cegid.alt}
                style={{ width: 80 }}
              />
            </Box>
            <Typography variant="h4" paragraph>
              {ts('page.organizationOwnerInvitation.title')}
            </Typography>
            <Typography variant="body1" paragraph>
              {ts('page.organizationOwnerInvitation.introValid', {
                account: accountLabel
              })}
            </Typography>
            <Box pt={2}>
              <Button
                variant="contained"
                color="primary"
                disabled={completing}
                onClick={onConfirm}
              >
                {ts('common.action.confirm')}
              </Button>
            </Box>
          </Box>
        </Card>
      </Grid>
    </Grid>
  );
};

export default OrganizationOwnerInvitation;
